import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@components/dialogs/confirm-dialog/confirm-dialog.component';
import {
  MaintenanceNotificationAttachmentI,
  MaintenanceNotificationI,
} from '@modules/maintenance-notification/interfaces/maintenance-notification.interface';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppUpdateHelperService {
  private attempts = 0;
  private timeoutResponse?: NodeJS.Timeout;

  constructor(private dialog: MatDialog, private translateService: TranslateService) {}

  public executeUpdate() {
    if (this.attempts === 0) {
      this.showDialog();
    }
  }

  public async executeMaintenanceNotification(notification: MaintenanceNotificationI, forceDialog = false) {
    if (!forceDialog && notification.expiration) {
      if (localStorage.getItem(`maintenance_${notification._id}`)) {
        return;
      }
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'custom-dialog-v2',
      width: '450px',
      disableClose: true,
      data: {
        confirmButtonText: 'UNDERSTOOD',
        cancelButtonText: '',
        showCloseButton: false,
        html: this.buildNotificationHtml(notification),
        title: notification.title,
      },
    });

    await firstValueFrom(dialogRef.afterClosed());

    if (notification.expiration) {
      localStorage.setItem(`maintenance_${notification._id}`, 'true');
    }
  }

  // Attachments render at the end of the notice, after the body. The dialog renders this
  // via bypassed innerHTML in a separate component, so styles must be inline (a scoped
  // class here would not apply there).
  private buildNotificationHtml(notification: MaintenanceNotificationI): string {
    const text = notification.text ?? '';
    const attachments = notification.attachments ?? [];

    if (!attachments.length) {
      return text;
    }

    const items = attachments.map((attachment, index) => this.renderAttachment(attachment, index)).join('');
    return `${text}${this.attachmentStyles()}<div style="margin-top:12px;display:flex;flex-wrap:wrap;gap:8px;">${items}</div>`;
  }

  // Injected once with the notice HTML (rendered via bypassed innerHTML, so scoped
  // component styles / :hover cannot reach it). Provides hover feedback and a CSS-only
  // lightbox (checkbox toggle) so clicking an image opens a larger preview without a new tab.
  private attachmentStyles(): string {
    return `<style>
.mnt-att{width:110px;border:1px solid #d0d2d3;border-radius:8px;overflow:hidden;text-decoration:none;display:block;cursor:pointer;transition:border-color .15s,box-shadow .15s,transform .15s;}
.mnt-att:hover{border-color:#7953d4;box-shadow:0 2px 8px rgba(76,46,212,.18);transform:translateY(-1px);}
.mnt-att__prev{width:100%;height:90px;object-fit:cover;display:block;}
.mnt-att__icon{height:90px;display:flex;align-items:center;justify-content:center;background:#f7f7f9;font-size:30px;}
.mnt-att__cap{padding:6px 8px;font-size:11px;color:#2f3232;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.mnt-lb-cb{display:none;}
.mnt-lb-ov{display:none;position:fixed;inset:0;background:rgba(0,0,0,.82);z-index:100000;align-items:center;justify-content:center;padding:24px;cursor:zoom-out;}
.mnt-lb-cb:checked~.mnt-lb-ov{display:flex;}
.mnt-lb-ov img{max-width:92vw;max-height:92vh;object-fit:contain;border-radius:6px;}
</style>`;
  }

  private renderAttachment(attachment: MaintenanceNotificationAttachmentI, index: number): string {
    const url = this.safeAttrUrl(attachment.url ?? '');
    const name = this.escapeHtml(attachment.name ?? '');
    const isImage = (attachment.type ?? '').startsWith('image/');
    const caption = `<div class="mnt-att__cap">${name}</div>`;

    if (isImage) {
      const id = `mnt-lb-${index}`;
      return (
        `<span style="display:contents">` +
        `<input type="checkbox" id="${id}" class="mnt-lb-cb" />` +
        `<label for="${id}" class="mnt-att" title="${name}"><img class="mnt-att__prev" src="${url}" alt="${name}" />${caption}</label>` +
        `<label for="${id}" class="mnt-lb-ov"><img src="${url}" alt="${name}" /></label>` +
        `</span>`
      );
    }

    return (
      `<a href="${url}" target="_blank" rel="noopener" download class="mnt-att" title="${name}">` +
      `<div class="mnt-att__icon">📄</div>${caption}</a>`
    );
  }

  // S3 Location already comes percent-encoded; re-encoding it (encodeURI, or decode+encode)
  // double-escapes reserved chars like ',' (%2C -> %252C) and 404s the asset. Keep the url
  // as-is, only neutralize quotes so they can't break out of the src/href attribute.
  private safeAttrUrl(url: string): string {
    return url.replace(/"/g, '%22');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private async showDialog(forceReload = false) {
    this.attempts++;
    this.initTimeoutResponse();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'custom-dialog-v2',
      width: '450px',
      disableClose: true,
      data: {
        message: 'SYSTEM_NEW_VERSION',
        confirmButtonText: 'UPDATE_NOW',
        cancelButtonText: forceReload ? '' : this.translateService.instant('WAIT_MINUTES').replace('{0}', '10'),
        showCloseButton: false,
      },
    });
    const update = await firstValueFrom(dialogRef.afterClosed());

    clearTimeout(this.timeoutResponse);

    if (update) {
      window.location.reload();
    } else {
      this.initTimeoutRetry();
    }
  }

  private initTimeoutResponse() {
    this.timeoutResponse = setTimeout(() => {
      window.location.reload();
    }, 30 * 60 * 1000); // 30 minutos
  }

  private initTimeoutRetry() {
    setTimeout(() => {
      if (this.attempts < 2) {
        this.showDialog();
      } else {
        this.showDialog(true);
      }
    }, 10 * 60 * 1000); // 10 minutos
  }
}
